import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Info } from '@/components/ui/Info'
import { Input } from '@/components/ui/Input'
import { useAuthStore } from '@/store/auth'
import { ApiError, userService } from '@services'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import styles from './ChangePassword.module.scss'

interface ChangePasswordFormData {
  newPassword: string
  confirmPassword: string
}

export const ChangePasswordPage = () => {
  const methods = useForm<ChangePasswordFormData>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data: ChangePasswordFormData) => {
    if (!user?.id) {
      setError('User not found. Please log in again.')
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      await userService.update(user.id, { password: data.newPassword })
      navigate('/settings', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.data?.message) {
        setError(err.data.message)
      } else {
        setError('Failed to update password. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.container} data-testid="change-password-page">
      <div className={styles.content}>
        <header className={styles.header}>
          <Button
            icon={<Icon name="chevron-left" size={20} />}
            onClick={() => navigate('/settings')}
            className={styles.backButton}
            testId="change-password-back-button"
          />
          <h1 className={styles.title}>Change Password</h1>
        </header>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className={styles.form}
            data-testid="change-password-form"
          >
            <Input<ChangePasswordFormData>
              name="newPassword"
              type="password"
              label="New Password"
              required
              testId="change-password-new"
              validation={{
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              }}
            />

            <Input<ChangePasswordFormData>
              name="confirmPassword"
              type="password"
              label="Confirm Password"
              required
              testId="change-password-confirm"
              validation={{
                validate: (value) =>
                  value === methods.getValues('newPassword') ||
                  'Passwords do not match',
              }}
            />

            {error && (
              <Info variant="error" testId="change-password-error">
                {error}
              </Info>
            )}

            <Button
              text={isSubmitting ? 'Updating...' : 'Update Password'}
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              testId="change-password-submit"
            />
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
